// src/pages/OffresDetails.js - VERSION AVEC QUOTAS
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const OffresDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [offre, setOffre] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [currentProfile, setCurrentProfile] = useState(null);

  // Palette de couleurs SecureControl
  const COLORS = {
    primary: '#0B3C5D',
    secondary: '#1E88C8',
    accent: '#4FC3F7',
    darkText: '#2E2E2E',
    lightBg: '#F5F7FA',
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentProfile(String(user.profile));
      } catch (e) {
        console.error("Erreur parsing user:", e);
      }
    }
    
    if (id) {
      fetchOffre();
    }
  }, [id]);

  // Récupérer les données de l'offre
  const fetchOffre = async () => {
    setLoading(true);
    setServerError("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      const url = `${API_BASE_URL}/offres/get.php?id=${id}`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success) {
        setOffre(response.data.offre);
      } else {
        setServerError(response.data?.message || "Offre non trouvée.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response?.status === 401) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setServerError("Accès interdit.");
      } else if (err.response?.status === 404) {
        setServerError("Offre non trouvée.");
      } else {
        setServerError("Erreur lors du chargement des données.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Formater le prix en FCFA
  const formatPrix = (montant) => {
    const montantNum = parseFloat(montant) || 0;
    const montantArrondi = Math.round(montantNum);
    
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montantArrondi);
  };

  // Formater les nombres
  const formatNombre = (nombre) => {
    return new Intl.NumberFormat('fr-FR').format(nombre || 0);
  };

  // Formater la durée
  const formatDuree = (dureeMois) => {
    if (dureeMois === 1) return '1 mois';
    if (dureeMois < 12) return `${dureeMois} mois`;
    if (dureeMois === 12) return '1 an';
    const annees = Math.floor(dureeMois / 12);
    const mois = dureeMois % 12;
    if (mois === 0) return `${annees} an${annees > 1 ? 's' : ''}`;
    return `${annees} an${annees > 1 ? 's' : ''} et ${mois} mois`;
  };

  // Obtenir l'icône selon le type
  const getTypeIcon = (type) => {
    switch(type) {
      case 'annuel': return 'bi-calendar-year';
      case 'mensuel': return 'bi-calendar-month';
      case 'trimestriel': return 'bi-calendar3';
      case 'semestriel': return 'bi-calendar2-range';
      default: return 'bi-calendar';
    }
  };

  // Obtenir la couleur selon le type
  const getTypeColor = (type) => {
    switch(type) {
      case 'annuel': return '#0B3C5D';
      case 'mensuel': return '#1E88C8';
      case 'trimestriel': return '#4FC3F7';
      case 'semestriel': return '#2196f3';
      default: return '#6c757d';
    }
  };

  // Obtenir le label du type de tags
  const getTypeTagsLabel = (type) => {
    switch(type) {
      case 'qr': return 'QR Code';
      case 'nfc': return 'NFC';
      case 'both': return 'QR + NFC';
      default: return 'Non spécifié';
    }
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString || dateString === '0000-00-00 00:00:00') return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Gérer la suppression
  const handleDelete = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'offre "${offre.libele_offre}" ?`)) {
      return;
    }

    try {
      const url = `${API_BASE_URL}/offres/delete.php`;
      const response = await axios.post(
        url,
        { id_offre: id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        alert(`Offre "${offre.libele_offre}" supprimée avec succès.`);
        navigate("/offres");
      } else {
        setServerError(response.data?.message || "Échec de la suppression.");
      }
    } catch (err) {
      console.error("Erreur détaillée:", err);
      
      if (err.response?.data?.message) {
        setServerError(err.response.data.message);
      } else if (err.response?.data?.error) {
        setServerError(err.response.data.error);
      } else {
        setServerError("Erreur technique lors de la suppression.");
      }
    }
  };

  // Gérer le changement de statut
  const handleChangeStatus = async () => {
    const newStatut = offre.statut_offre === 1 ? 0 : 1;
    const action = newStatut === 1 ? "activer" : "désactiver";
    
    if (!window.confirm(`Êtes-vous sûr de vouloir ${action} l'offre "${offre.libele_offre}" ?`)) {
      return;
    }

    try {
      const url = `${API_BASE_URL}/offres/changer_status.php`;
      const response = await axios.post(
        url,
        { id_offre: id, statut_offre: newStatut },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data?.success) {
        const result = newStatut === 1 ? "activée" : "désactivée";
        alert(`Offre "${offre.libele_offre}" ${result} avec succès.`);
        fetchOffre();
      } else {
        setServerError(response.data?.message || "Échec de l'opération.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      setServerError("Erreur technique.");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails de l'offre</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/offres">Offres</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Détails</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-3" style={{ color: COLORS.darkText }}>Chargement des données de l'offre...</p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (!offre) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails de l'offre</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Tableau de bord</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/offres">Offres</Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Détails</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#F5F7FA',
                  color: '#B0BEC5'
                }}>
                  <i className="bi bi-exclamation-circle fs-1"></i>
                </div>
              </div>
              <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Offre non trouvée</h5>
              <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                L'offre que vous recherchez n'existe pas ou a été supprimée.
              </p>
              
              <button
                className="btn fw-semibold mt-4"
                onClick={() => navigate("/offres")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  padding: '10px 24px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = COLORS.white;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = COLORS.primary;
                }}
              >
                <i className="bi bi-arrow-left me-2"></i>
                Retour aux offres
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  const typeColor = getTypeColor(offre.type_offre);
  const prixMensuel = offre.montant / offre.duree_mois;
  const typeTagsLabel = getTypeTagsLabel(offre.type_tags);

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Détails de l'offre</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/offres">Offres</Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>{offre.libele_offre}</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        {serverError && (
          <div className="alert alert-dismissible fade show mb-4" style={{ 
            backgroundColor: '#ffebee',
            borderColor: COLORS.danger,
            color: '#c62828',
            borderRadius: '8px',
            borderLeft: `4px solid ${COLORS.danger}`
          }}>
            <div className="d-flex align-items-center">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <div className="flex-grow-1">{serverError}</div>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setServerError("")}
              ></button>
            </div>
            <div className="mt-3">
              <button 
                className="btn btn-sm fw-semibold"
                onClick={() => fetchOffre()}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: 'transparent',
                  borderRadius: '6px',
                  padding: '6px 16px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = COLORS.white;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = COLORS.primary;
                }}
              >
                <i className="bi bi-arrow-clockwise me-1"></i> 
                Réessayer
              </button>
            </div>
          </div>
        )}

        <div className="row">
          {/* Colonne principale - Détails de l'offre */}
          <div className="col-lg-8">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              marginBottom: '24px'
            }}>
              {/* En-tête de la carte */}
              <div className="card-header border-0 pb-0" style={{ 
                backgroundColor: typeColor,
                color: COLORS.white,
                padding: '24px',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 className="card-title mb-2" style={{ 
                      color: COLORS.white,
                      fontSize: '1.5rem',
                      fontWeight: '600'
                    }}>
                      {offre.libele_offre}
                    </h3>
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      <div className="d-flex align-items-center">
                        <i className={`bi ${getTypeIcon(offre.type_offre)} me-2`}></i>
                        <span className="badge fw-semibold" style={{ 
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          color: COLORS.white,
                          fontSize: '0.8rem',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          textTransform: 'uppercase'
                        }}>
                          {offre.type_offre}
                        </span>
                      </div>
                      <span className="badge fw-semibold" style={{ 
                        backgroundColor: offre.statut_offre === 1 ? 'rgba(76, 175, 80, 0.2)' : 'rgba(117, 117, 117, 0.2)',
                        color: offre.statut_offre === 1 ? '#e8f5e9' : '#f5f5f5',
                        fontSize: '0.8rem',
                        padding: '6px 12px',
                        borderRadius: '6px'
                      }}>
                        <i className={`bi ${offre.statut_offre === 1 ? 'bi-check-circle' : 'bi-x-circle'} me-1`}></i>
                        {offre.statut_offre === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="text-end">
                    <div className="fw-bold" style={{ fontSize: '1.8rem', marginBottom: '4px' }}>
                      {formatPrix(offre.montant)}
                    </div>
                    <small style={{ opacity: 0.9, fontSize: '0.9rem' }}>
                      Prix total
                    </small>
                  </div>
                </div>
              </div>
              
              {/* Corps de la carte */}
              <div className="card-body">
                {/* Description */}
                <div className="mb-5">
                  <h6 className="mb-3" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600',
                    fontSize: '1.1rem'
                  }}>
                    <i className="bi bi-text-paragraph me-2" style={{ color: COLORS.secondary }}></i>
                    Description
                  </h6>
                  <div className="p-3 rounded" style={{ 
                    backgroundColor: COLORS.lightBg,
                    borderLeft: `4px solid ${COLORS.secondary}`
                  }}>
                    <p style={{ color: COLORS.darkText, marginBottom: '0', lineHeight: '1.6' }}>
                      {offre.description || (
                        <span className="text-muted">
                          <i className="bi bi-info-circle me-1"></i>
                          Aucune description n'a été ajoutée pour cette offre.
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Quotas */}
                <div className="mb-5">
                  <h6 className="mb-3" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600',
                    fontSize: '1.1rem'
                  }}>
                    <i className="bi bi-pie-chart me-2" style={{ color: COLORS.secondary }}></i>
                    Quotas inclus
                  </h6>
                  
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <div className="card h-100" style={{ 
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        backgroundColor: '#F5F7FA'
                      }}>
                        <div className="card-body text-center">
                          <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                            width: '50px',
                            height: '50px',
                            backgroundColor: '#E3F2FD',
                            color: COLORS.primary
                          }}>
                            <i className="bi bi-people fs-3"></i>
                          </div>
                          <h5 className="fw-bold" style={{ color: COLORS.primary, fontSize: '1.8rem' }}>
                            {formatNombre(offre.max_clients)}
                          </h5>
                          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                            Clients max
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4 mb-3">
                      <div className="card h-100" style={{ 
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        backgroundColor: '#F5F7FA'
                      }}>
                        <div className="card-body text-center">
                          <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                            width: '50px',
                            height: '50px',
                            backgroundColor: '#E3F2FD',
                            color: COLORS.primary
                          }}>
                            <i className="bi bi-building fs-3"></i>
                          </div>
                          <h5 className="fw-bold" style={{ color: COLORS.primary, fontSize: '1.8rem' }}>
                            {formatNombre(offre.max_sites)}
                          </h5>
                          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                            Sites max
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-4 mb-3">
                      <div className="card h-100" style={{ 
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        backgroundColor: '#F5F7FA'
                      }}>
                        <div className="card-body text-center">
                          <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                            width: '50px',
                            height: '50px',
                            backgroundColor: '#E3F2FD',
                            color: COLORS.primary
                          }}>
                            <i className={`bi ${offre.type_tags === 'qr' ? 'bi-qr-code' : offre.type_tags === 'nfc' ? 'bi-wifi' : 'bi-tags'} fs-3`}></i>
                          </div>
                          <h6 className="fw-bold" style={{ color: COLORS.primary, fontSize: '1rem' }}>
                            {typeTagsLabel}
                          </h6>
                          {offre.max_tags_qr > 0 && (
                            <div className="mt-2">
                              <span className="badge fw-semibold" style={{ 
                                backgroundColor: '#E3F2FD',
                                color: COLORS.primary,
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '0.8rem'
                              }}>
                                <i className="bi bi-qr-code me-1"></i>
                                QR: {formatNombre(offre.max_tags_qr)}
                              </span>
                            </div>
                          )}
                          {offre.max_tags_nfc > 0 && (
                            <div className="mt-2">
                              <span className="badge fw-semibold" style={{ 
                                backgroundColor: '#E3F2FD',
                                color: COLORS.primary,
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '0.8rem'
                              }}>
                                <i className="bi bi-wifi me-1"></i>
                                NFC: {formatNombre(offre.max_tags_nfc)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Détails techniques */}
                <div className="row mb-5">
                  <div className="col-md-6">
                    <div className="card h-100" style={{ 
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px'
                    }}>
                      <div className="card-body">
                        <h6 className="card-subtitle mb-3" style={{ 
                          color: COLORS.primary,
                          fontWeight: '600',
                          fontSize: '0.95rem'
                        }}>
                          <i className="bi bi-clock-history me-2" style={{ color: COLORS.secondary }}></i>
                          Durée de l'abonnement
                        </h6>
                        <div className="text-center py-2">
                          <div className="fw-bold" style={{ 
                            color: COLORS.primary,
                            fontSize: '1.8rem',
                            marginBottom: '4px'
                          }}>
                            {offre.duree_mois}
                          </div>
                          <div style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                            mois
                          </div>
                          <div className="mt-2">
                            <span className="badge fw-semibold" style={{ 
                              backgroundColor: '#E3F2FD',
                              color: COLORS.primary,
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '0.85rem'
                            }}>
                              <i className="bi bi-calendar-check me-1"></i>
                              {formatDuree(offre.duree_mois)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="card h-100" style={{ 
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px'
                    }}>
                      <div className="card-body">
                        <h6 className="card-subtitle mb-3" style={{ 
                          color: COLORS.primary,
                          fontWeight: '600',
                          fontSize: '0.95rem'
                        }}>
                          <i className="bi bi-calculator me-2" style={{ color: COLORS.secondary }}></i>
                          Calcul du prix mensuel
                        </h6>
                        <div className="text-center py-2">
                          <div className="fw-bold" style={{ 
                            color: COLORS.primary,
                            fontSize: '1.8rem',
                            marginBottom: '4px'
                          }}>
                            {formatPrix(prixMensuel)}
                          </div>
                          <div style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                            par mois
                          </div>
                          <div className="mt-2">
                            <small className="text-muted">
                              {formatPrix(offre.montant)} ÷ {offre.duree_mois} mois
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Métadonnées */}
                <div className="row mt-4">
                  <div className="col-md-6">
                    <div className="d-flex align-items-center mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-calendar-plus"></i>
                      </div>
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: '0.8rem' }}>Date de création</small>
                        <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          {formatDate(offre.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-md-6">
                    <div className="d-flex align-items-center mb-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-calendar-check"></i>
                      </div>
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: '0.8rem' }}>Dernière modification</small>
                        <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          {offre.updated_at && offre.updated_at !== '0000-00-00 00:00:00' ? 
                            formatDate(offre.updated_at) : 
                            'Aucune modification'
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne latérale - Actions et informations */}
          <div className="col-lg-4">
            {/* Actions sous forme d'icônes */}
            {currentProfile === "1" && (
              <div className="card mb-4" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body">
                  <h6 className="card-title mb-3" style={{ 
                    color: COLORS.primary,
                    fontWeight: '600',
                    fontSize: '1.1rem'
                  }}>
                    <i className="bi bi-gear me-2" style={{ color: COLORS.secondary }}></i>
                    Gestion de l'offre
                  </h6>
                  
                  <div className="d-flex justify-content-center gap-3">
                    {/* Bouton Modifier */}
                    <Link
                      to={`/offres/edit/${id}`}
                      className="btn fw-semibold d-flex flex-column align-items-center justify-content-center"
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '10px',
                        padding: '12px',
                        width: '80px',
                        height: '80px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.color = COLORS.white;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.primary;
                      }}
                      title="Modifier cette offre"
                    >
                      <i className="bi bi-pencil-square fs-4 mb-2"></i>
                      <small className="fw-semibold">Modifier</small>
                    </Link>
                    
                    {/* Bouton Activer/Désactiver */}
                    <button
                      className="btn fw-semibold d-flex flex-column align-items-center justify-content-center"
                      onClick={handleChangeStatus}
                      style={{
                        color: offre.statut_offre === 1 ? COLORS.warning : COLORS.success,
                        borderColor: offre.statut_offre === 1 ? COLORS.warning : COLORS.success,
                        backgroundColor: 'transparent',
                        borderRadius: '10px',
                        padding: '12px',
                        width: '80px',
                        height: '80px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = offre.statut_offre === 1 ? COLORS.warning : COLORS.success;
                        e.target.style.color = COLORS.white;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = offre.statut_offre === 1 ? COLORS.warning : COLORS.success;
                      }}
                      title={offre.statut_offre === 1 ? "Désactiver l'offre" : "Activer l'offre"}
                    >
                      <i className={`bi ${offre.statut_offre === 1 ? 'bi-toggle-off' : 'bi-toggle-on'} fs-4 mb-2`}></i>
                      <small className="fw-semibold">{offre.statut_offre === 1 ? 'Désactiver' : 'Activer'}</small>
                    </button>
                    
                    {/* Bouton Supprimer */}
                    <button
                      className="btn fw-semibold d-flex flex-column align-items-center justify-content-center"
                      onClick={handleDelete}
                      style={{
                        color: COLORS.danger,
                        borderColor: COLORS.danger,
                        backgroundColor: 'transparent',
                        borderRadius: '10px',
                        padding: '12px',
                        width: '80px',
                        height: '80px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.danger;
                        e.target.style.color = COLORS.white;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.danger;
                      }}
                      title="Supprimer cette offre"
                    >
                      <i className="bi bi-trash fs-4 mb-2"></i>
                      <small className="fw-semibold">Supprimer</small>
                    </button>
                  </div>
                  
                  {/* Bouton Retour */}
                  <div className="mt-4">
                    <Link
                      to="/offres"
                      className="btn fw-semibold w-100"
                      style={{
                        color: COLORS.primary,
                        borderColor: '#e0e0e0',
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '12px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.lightBg;
                        e.target.style.borderColor = COLORS.primary;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.borderColor = '#e0e0e0';
                      }}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Retour à la liste
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Informations */}
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h6 className="card-title mb-3" style={{ 
                  color: COLORS.primary,
                  fontWeight: '600',
                  fontSize: '1.1rem'
                }}>
                  <i className="bi bi-info-circle me-2" style={{ color: COLORS.secondary }}></i>
                  Informations
                </h6>
                
                <ul className="list-unstyled mb-0">
                  <li className="mb-3">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-tags"></i>
                      </div>
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: '0.8rem' }}>Type d'abonnement</small>
                        <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          {offre.type_offre.charAt(0).toUpperCase() + offre.type_offre.slice(1)}
                        </div>
                      </div>
                    </div>
                  </li>
                  
                  <li className="mb-3">
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-calendar-week"></i>
                      </div>
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: '0.8rem' }}>Périodicité</small>
                        <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          {offre.duree_mois === 1 ? 'Mensuelle' : 
                           offre.duree_mois === 3 ? 'Trimestrielle' :
                           offre.duree_mois === 6 ? 'Semestrielle' :
                           offre.duree_mois === 12 ? 'Annuelle' : 'Personnalisée'}
                        </div>
                      </div>
                    </div>
                  </li>
                  
                  <li>
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                        width: '24px',
                        height: '24px',
                        backgroundColor: '#E3F2FD',
                        color: COLORS.primary
                      }}>
                        <i className="bi bi-shield-check"></i>
                      </div>
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: '0.8rem' }}>Statut</small>
                        <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                          {offre.statut_offre === 1 ? (
                            <span className="text-success">
                              <i className="bi bi-eye me-1"></i>
                              Visible
                            </span>
                          ) : (
                            <span className="text-warning">
                              <i className="bi bi-eye-slash me-1"></i>
                              Masquée
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>

                  {/* Statistiques pour admin */}
                  {currentProfile === "1" && offre.statistiques && (
                    <li className="mt-3 pt-3 border-top">
                      <div className="d-flex align-items-center">
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                          width: '24px',
                          height: '24px',
                          backgroundColor: '#E3F2FD',
                          color: COLORS.primary
                        }}>
                          <i className="bi bi-graph-up"></i>
                        </div>
                        <div>
                          <small className="text-muted d-block" style={{ fontSize: '0.8rem' }}>Statistiques</small>
                          <div style={{ color: COLORS.darkText, fontWeight: '500' }}>
                            {offre.statistiques.nombre_souscriptions} souscription(s)
                          </div>
                          {offre.statistiques.nombre_expires > 0 && (
                            <div><small className="text-muted">{offre.statistiques.nombre_expires} expirée(s)</small></div>
                          )}
                        </div>
                      </div>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            {/* Note pour les utilisateurs non-admins */}
            {currentProfile !== "1" && (
              <div className="card mt-4" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none',
                backgroundColor: '#FFF3E0'
              }}>
                <div className="card-body">
                  <div className="d-flex align-items-start">
                    <div className="me-3">
                      <i className="bi bi-info-circle fs-4" style={{ color: COLORS.warning }}></i>
                    </div>
                    <div>
                      <h6 className="mb-2" style={{ 
                        color: COLORS.darkText,
                        fontWeight: '600',
                        fontSize: '1rem'
                      }}>
                        Information
                      </h6>
                      <p className="mb-0" style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                        Pour souscrire à cette offre ou obtenir plus d'informations, contactez votre administrateur système.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .table,
        small,
        .alert,
        p {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge,
        .card {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .badge {
          font-weight: 500;
        }
        
        .progress-bar {
          transition: width 0.6s ease;
        }
        
        /* Tooltips pour les boutons */
        [title] {
          position: relative;
        }
        
        [title]:hover::after {
          content: attr(title);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background-color: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          white-space: nowrap;
          z-index: 1000;
          margin-bottom: 5px;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.gap-3 {
            flex-wrap: wrap;
            justify-content: center;
          }
          
          .row .col-lg-8,
          .row .col-lg-4 {
            margin-bottom: 24px;
          }
          
          /* Adaptation des boutons icônes sur mobile */
          .btn[style*="width: 80px"] {
            width: 70px !important;
            height: 70px !important;
          }
        }
        
        /* Amélioration de la lisibilité */
        .card-header h3 {
          text-shadow: 0 1px 2px rgba(0,0,0,0.1);
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-1px);
        }
        
        /* Style pour les listes */
        .list-unstyled li {
          border-bottom: 1px solid ${COLORS.lightBg};
          padding-bottom: 12px;
        }
        
        .list-unstyled li:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
        
        /* Animation pour les cartes */
        .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0,0,0,0.12) !important;
        }
        
        /* Style pour les sections avec bordures */
        [style*="border-left"] {
          padding-left: 16px !important;
        }
        
        /* Effet de survol pour les boutons icônes */
        .btn[style*="width: 80px"]:hover {
          transform: translateY(-4px) scale(1.05);
          box-shadow: 0 6px 12px rgba(0,0,0,0.15);
        }
      `}</style>
    </Layout>
  );
};

export default OffresDetails;